import React, { useState, useEffect, useRef } from "react";
import { useIsMobile } from "../hooks/is.mobile";
import "../css/image.strip.css";

function smoothScrollTo(
  container: HTMLElement,
  target: number,
  duration = 1000
) {
  const start = container.scrollLeft;
  const change = target - start;
  const startTime = performance.now();

  function easeInOutQuad(t: number) {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  }

  function animate(time: number) {
    const elapsed = time - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const ease = easeInOutQuad(progress);

    container.scrollLeft = start + change * ease;

    if (elapsed < duration) {
      requestAnimationFrame(animate);
    }
  }

  requestAnimationFrame(animate);
}

interface ImageStripProps {
  images: any[];
  onClick?: () => void;
  imageClass?: string;
}

export const CenteredImageStrip: React.FC<ImageStripProps> = ({
  images,
  onClick,
  imageClass,
}) => {
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const minSwipeDistance = 50; // minimum swipe distance in px

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (
      touchStartX.current !== null &&
      touchEndX.current !== null &&
      Math.abs(touchStartX.current - touchEndX.current) > minSwipeDistance
    ) {
      if (touchStartX.current > touchEndX.current)
        setIndex(index < images.length - 1 ? index + 1 : index);
      else setIndex(index > 0 ? index - 1 : index);
    }
    // reset
    touchStartX.current = null;
    touchEndX.current = null;
  };

  const isMobile = useIsMobile();

  const [index, setIndex] = useState<number>(0);
  const [updateCount, setUpdateCount] = useState(0);
  const forceUpdate = React.useRef(0);
  const setIndexForce = (newIndex: number) => {
    setIndex(newIndex);
    forceUpdate.current += 1;
    setUpdateCount(forceUpdate.current);
  };

  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLImageElement | null)[]>([]);

  useEffect(() => {
    const interval = setInterval(
      () => {
        setIndex((prev) => {
          if (prev >= images.length - 1) return prev;
          return prev + 1;
        });
      },
      isMobile ? 8000 : 5000
    );
    return () => clearInterval(interval);
  }, [index, updateCount]);

  useEffect(() => {
    const container = containerRef.current;
    const item = itemRefs.current[index];
    if (container && item) {
      requestAnimationFrame(() => {
        const containerCenter = container.offsetWidth / 2;
        const itemCenter =
          item.offsetLeft - container.scrollLeft + item.offsetWidth / 2;
        const scrollTo = container.scrollLeft + (itemCenter - containerCenter);

        smoothScrollTo(container, scrollTo, isMobile ? 375 : 750); // 1500ms = 1.5s duration
      });
    }
  }, [index]);

  return (
    <div
      ref={containerRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        display: "flex",
        whiteSpace: "nowrap",
        overflowX: "auto",
        width: "100vw", // enforce container width
        touchAction: "none", //moze bit pan-x ako je bas scroll ali ne zelin da se scrolla nego swipea
      }}
    >
      {images.map((src: any, i: number) => (
        <div
          style={{
            flex: isMobile ? "0 0 80vw" : "0 0 40vw", // fixed width flex item, no shrinking
            scrollSnapAlign: "center", // if you use scroll snap
            alignSelf: "center",
            textAlign: "center",
            paddingLeft: "0.5vw",
            paddingRight: "0.5vw",
          }}
          key={src}
        >
          <img
            key={i}
            ref={(el) => {
              itemRefs.current[i] = el;
            }}
            onClick={
              isMobile
                ? () => {
                    forceUpdate.current += 1;
                    setUpdateCount(forceUpdate.current);
                  }
                : () => {
                    setIndexForce(i);
                    onClick?.();
                  }
            }
            src={src}
            alt={`Image ${i}`}
            className={imageClass ? imageClass : "image-strip-image"}
          />
        </div>
      ))}
    </div>
  );
};
